// src/lib/catalog.ts
//
// Catalog types and pure normalization/grouping helpers for the MCP Registry.
//
// The catalog is sourced at runtime from the platform
// (`GET /api/v1/registry/mcp-catalog`) and carries placeholders only — never a
// literal token. `normalizeCatalog` turns the raw API shape into validated
// `CatalogEntry` items, `isSupportedServerKind` is the predicate the UI uses to
// decide between a normal card and an unsupported card, and `groupByCategory`
// produces deterministic, first-appearance-ordered Category groups.

/** Whether a server is a remote HTTP server or a local command (stdio) server. */
export type ServerKind = 'remote-http' | 'local-command'

/** How an entry's credentials are referenced (placeholders only — never literals). */
export type TokenStrategy =
  | { kind: 'bearer' } // remote-http: Authorization: Bearer <per-IDE placeholder>
  | { kind: 'env-placeholders' } // local-command: env map values are already placeholders

/**
 * One MCP server in the catalog. Data-only; adding one is a new YAML file.
 *
 * `serverKind` is typed as `ServerKind | (string & {})` so `normalizeCatalog`
 * can preserve an otherwise-complete entry that declares an unknown server kind
 * (rendered as unsupported) while `isSupportedServerKind` narrows known values.
 */
export interface CatalogEntry {
  id: string // stable id == generated server entry name
  name: string // display name
  description: string
  category: string // display label, e.g. "Issue Tracking"
  serverKind: ServerKind | (string & {})
  tokenStrategy: TokenStrategy
  homepage?: string
  /** Present (and required) only for serverKind === 'local-command'. */
  command?: string // e.g. "uvx"
  args?: string[] // e.g. ["mcp-atlassian"]
  env?: Record<string, string> // placeholder-only values
}

/** The raw shape returned by GET /api/v1/registry/mcp-catalog. */
export interface RawCatalogServer {
  id?: string
  name?: string
  description?: string
  category?: string
  serverKind?: string
  tokenStrategy?: { kind?: string }
  homepage?: string
  command?: string
  args?: string[]
  env?: Record<string, string>
}

/** The response envelope returned by GET /api/v1/registry/mcp-catalog. */
export interface McpCatalogResponse {
  servers: RawCatalogServer[]
}

/** True iff `value` is a non-empty string after trimming. */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

/** True iff `strategy` is a usable `TokenStrategy` of a recognized kind. */
function isTokenStrategy(strategy: RawCatalogServer['tokenStrategy']): strategy is TokenStrategy {
  return (
    typeof strategy === 'object' &&
    strategy !== null &&
    (strategy.kind === 'bearer' || strategy.kind === 'env-placeholders')
  )
}

/**
 * Explain why a raw server is unusable, or return `null` when it is valid.
 *
 * Centralizes the drop rules so both the filtering and the transparency warning
 * (see `normalizeCatalog`) share one source of truth.
 */
function dropReason(server: RawCatalogServer): string | null {
  if (server === null || typeof server !== 'object') {
    return 'entry is not an object'
  }
  if (!isNonEmptyString(server.id)) {
    return 'missing/empty "id"'
  }
  if (!isNonEmptyString(server.name)) {
    return 'missing/empty "name"'
  }
  if (!isNonEmptyString(server.serverKind)) {
    return 'missing/empty "serverKind"'
  }
  if (!isTokenStrategy(server.tokenStrategy)) {
    return 'missing/unrecognized "tokenStrategy" (expected { kind: "bearer" | "env-placeholders" })'
  }
  // A local-command server is unusable without a command to launch.
  if (server.serverKind === 'local-command' && !isNonEmptyString(server.command)) {
    return 'local-command entry is missing/empty "command"'
  }
  return null
}

/**
 * Normalize the raw catalog response into validated `CatalogEntry` items.
 *
 * Returns one `CatalogEntry` per VALID raw server, in input order. A raw server
 * is DROPPED when it lacks a non-empty `id`, a non-empty `name`, a recognized
 * `tokenStrategy`, a `serverKind`, or — for serverKind `local-command` — a
 * non-empty `command`.
 *
 * An entry that is otherwise complete but declares an UNKNOWN/unrecognized
 * `serverKind` string is PRESERVED (its `serverKind` is carried through as-is)
 * so the UI can render an unsupported-server indication rather than silently
 * dropping it. An entry missing `serverKind` entirely is dropped.
 *
 * Transparency: a dropped entry signals a misconfigured source extension, so
 * each drop is reported via `console.warn` (id + reason) instead of vanishing
 * silently — this lets a developer/operator notice and fix the source. The
 * warning is the only side effect; the function never throws, never mutates
 * `raw`, and is deterministic in its return value.
 */
export function normalizeCatalog(raw: McpCatalogResponse): CatalogEntry[] {
  const servers = raw && Array.isArray(raw.servers) ? raw.servers : []
  const entries: CatalogEntry[] = []
  const dropped: { id: string; reason: string }[] = []

  for (const server of servers) {
    const reason = dropReason(server)
    if (reason !== null) {
      const id =
        server !== null && typeof server === 'object' && typeof server.id === 'string'
          ? server.id
          : '<unknown>'
      dropped.push({ id, reason })
      continue
    }

    // `dropReason` returned null above, so id/name/serverKind are present and
    // tokenStrategy is a recognized kind — assert those facts for the types.
    const entry: CatalogEntry = {
      id: server.id as string,
      name: server.name as string,
      description: typeof server.description === 'string' ? server.description : '',
      category: isNonEmptyString(server.category) ? server.category : '',
      serverKind: server.serverKind as string,
      tokenStrategy: server.tokenStrategy as TokenStrategy,
    }

    if (isNonEmptyString(server.homepage)) {
      entry.homepage = server.homepage
    }
    if (isNonEmptyString(server.command)) {
      entry.command = server.command
    }
    if (Array.isArray(server.args)) {
      entry.args = [...server.args]
    }
    if (server.env !== null && typeof server.env === 'object') {
      entry.env = { ...server.env }
    }

    entries.push(entry)
  }

  if (dropped.length > 0) {
    // Surface misconfigured source extensions instead of hiding them. This is a
    // diagnostic only — it does not alter the returned catalog.
    console.warn(
      `[mcp-registry] normalizeCatalog dropped ${dropped.length} malformed catalog ` +
        `entr${dropped.length === 1 ? 'y' : 'ies'} (will NOT appear in the registry). ` +
        `Fix the source extension(s):`,
      dropped,
    )
  }

  return entries
}

/** True iff `entry.serverKind` is a kind the Config_Generator supports. */
export function isSupportedServerKind(entry: CatalogEntry): boolean {
  return entry.serverKind === 'remote-http' || entry.serverKind === 'local-command'
}

/**
 * Group catalog entries by Category.
 *
 * Category groups are ordered by the order in which each Category first appears
 * in `entries`, and entries within a group keep their input order. Pure and
 * deterministic: an identical input yields an identical grouping.
 */
export function groupByCategory(entries: CatalogEntry[]): { category: string; entries: CatalogEntry[] }[] {
  const groups: { category: string; entries: CatalogEntry[] }[] = []
  const byCategory = new Map<string, { category: string; entries: CatalogEntry[] }>()

  for (const entry of entries) {
    let group = byCategory.get(entry.category)
    if (group === undefined) {
      group = { category: entry.category, entries: [] }
      byCategory.set(entry.category, group)
      groups.push(group)
    }
    group.entries.push(entry)
  }

  return groups
}

/**
 * The `sdlc-platform` remote-HTTP catalog entry, available as a client-side
 * constant so QuickStart can render the platform install affordance even if the
 * catalog endpoint is briefly unavailable.
 *
 * No base URL is hardcoded here — the URL is resolved at generation time via
 * `resolveMcpBaseUrl()`.
 */
export const PLATFORM_ENTRY: CatalogEntry = {
  id: 'sdlc-platform',
  name: 'KONE SDLC Platform',
  description: 'Org rules, agents, skills, and project context.',
  category: 'Platform',
  serverKind: 'remote-http',
  tokenStrategy: { kind: 'bearer' },
}
